import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { blogs } from '../../data/blogs';
import BlogLayout from './BlogLayout';

const categoryColors = {
  teal: 'bg-teal-100 text-teal-700',
  yellow: 'bg-yellow-100 text-yellow-700',
};

const BlogCard = ({ post }) => (
  <article className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 flex flex-col overflow-hidden border border-gray-100">
    {/* Colored top strip as visual replacement for a cover image */}
    <div className="h-2 bg-teal-600" />

    <div className="p-6 flex flex-col flex-1">
      {/* Category + read time */}
      <div className="flex items-center justify-between mb-3">
        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${categoryColors[post.card.categoryColor] || categoryColors.teal}`}>
          {post.card.category}
        </span>
        <span className="flex items-center text-gray-400 text-xs">
          <Clock className="h-3 w-3 mr-1" />
          {post.readingTimeMinutes} min read
        </span>
      </div>

      <h2 className="text-lg font-bold text-gray-900 mb-3 leading-snug hover:text-teal-700 transition-colors">
        <Link to={`/blog/${post.slug}`}>{post.card.title}</Link>
      </h2>

      <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-6">
        {post.card.excerpt}
      </p>

      <Link
        to={`/blog/${post.slug}`}
        className="inline-flex items-center text-teal-600 font-semibold text-sm hover:text-teal-800 transition-colors group"
      >
        Read Article
        <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  </article>
);

const BlogListContent = ({ onBookingOpen }) => (
  <>
    {/* Hero */}
    <section className="bg-teal-700 py-16 px-4">
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="text-3xl md:text-5xl font-black text-white mb-4">
          Career Guidance Blog
        </h1>
        <p className="text-yellow-300 text-lg md:text-xl mb-8">
          Practical advice for students, graduates, and professionals in Bangalore, Jamshedpur, and Ranchi
        </p>
        <button
          onClick={onBookingOpen}
          className="px-8 py-3 bg-yellow-500 text-black font-bold rounded-md hover:bg-yellow-400 transition-colors"
        >
          Book a Free Career Session
        </button>
      </div>
    </section>

    {/* Blog grid */}
    <section className="py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogs.map((post) => (
            <BlogCard key={post.slug} post={post} />
          ))}
        </div>
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="bg-teal-50 border-t border-teal-100 py-12 px-4 text-center">
      <h2 className="text-2xl font-bold text-gray-900 mb-3">
        Want personalised career guidance?
      </h2>
      <p className="text-gray-600 mb-6">
        Reading is a good start. A free session with our counsellor is better.
      </p>
      <button
        onClick={onBookingOpen}
        className="px-8 py-3 bg-teal-600 text-white font-bold rounded-md hover:bg-teal-700 transition-colors"
      >
        Book Your Free Session
      </button>
    </section>
  </>
);

const BlogList = () => (
  <>
    <Helmet>
      <title>Career Counselling Blog | Guidance for Students and Professionals | Nirvana</title>
      <meta
        name="description"
        content="Practical career guidance articles for students and professionals in Bangalore, Jamshedpur, and Ranchi. Covering psychometric testing, stream selection, career changes, and more."
      />
      <meta
        name="keywords"
        content="career counselling blog, career guidance articles, career advice india, career counselling bangalore blog, career guidance jharkhand"
      />
      <link rel="canonical" href="https://nirvanacareercounselling.com/blog" />
      <meta property="og:title" content="Career Counselling Blog | Nirvana Career Counselling" />
      <meta property="og:description" content="Practical career guidance for students and professionals across Bangalore, Jamshedpur, and Ranchi." />
      <meta property="og:url" content="https://nirvanacareercounselling.com/blog" />
      <meta property="og:type" content="website" />
    </Helmet>
    <BlogLayout>
      <BlogListContent />
    </BlogLayout>
  </>
);

export default BlogList;
